import * as React from "react"
import {
  IconBell,
  IconCreditCard,
  IconFileText,
  IconHelp,
  IconKey,
  IconSettings,
  IconUsers,
} from "@tabler/icons-react"
import { GrAnalytics } from "react-icons/gr";
import { MdOutlineDashboardCustomize, MdOutlineDashboard } from "react-icons/md";


import { NavMain } from "@/components/nav-main"
import { NavOrganization } from "@/components/nav-organization"
import { NavSecondary } from "@/components/nav-secondary"
import { NavUser } from "@/components/nav-user"
import { useAuth } from "@/hooks/useAuth"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { TbZoomMoney } from "react-icons/tb";
import Logo from "@/components/Logo"
import { PiListMagnifyingGlassDuotone } from "react-icons/pi";

const data = {
  user: {
    name: "shadcn",
    email: "m@example.com",
    avatar: "/avatars/shadcn.jpg",
  },
  navMain: [
    {
      title: "Overview",
      url: "/overview",
      icon: MdOutlineDashboard,
    },
    {
      title: "Cost Explorer",
      url: "/breakdown",
      icon: TbZoomMoney,
      items: [
        {
          title: "Breakdown",
          url: "/breakdown",
          icon: GrAnalytics,
        },
        {
          title: "Activity",
          url: "/activity",
          icon: PiListMagnifyingGlassDuotone,
        },
      ]
    },
    {
      title: "Custom Dashboards",
      url: "/custom-dashboards",
      icon: MdOutlineDashboardCustomize,
      comingSoon: false,
      requiresUpgrade: true,
    },
    {
      title: "Reports",
      url: "/reports",
      icon: IconFileText,
    },
    {
      title: "Alerts",
      url: "/alerts",
      icon: IconBell,
    },
  ],
  navSecondary: [
    {
      title: "Get Help",
      url: "mailto:ajdin@fenra.io",
      icon: IconHelp,
    },
  ],
  organization: [
    {
      name: "API Keys",
      url: "/organization/api-keys",
      icon: IconKey,
    },
    {
      name: "Team",
      url: "/organization/team",
      icon: IconUsers,
    },
    {
      name: "Settings",
      url: "/organization/settings",
      icon: IconSettings,
    },
    {
      name: "Billing",
      url: "/organization/billing",
      icon: IconCreditCard,
    },
  ],
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, organization, isOnFreePlan } = useAuth()

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const userData = user
    ? {
      name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      avatar: "",
      initials: getInitials(user.first_name, user.last_name),
    }
    : {
      name: "Guest",
      email: "",
      avatar: "",
      initials: "G",
    }

  // Filter organization items based on user role
  const organizationItems = React.useMemo(() => {
    if (!organization) return data.organization

    if (organization?.role === 'VIEWER') {
      // Viewers cannot see any organization tabs
      return []
    }

    if (organization?.role === 'OWNER') {
      // Owners can see everything
      return data.organization
    }

    // Admins can see everything except Billing
    return data.organization.filter(item => item.name !== 'Billing')
  }, [organization?.role])

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="group-data-[collapsible=icon]:px-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2"
            >
              <a href="#">
                <Logo className="!size-5" showText={false} />
                <span className="text-xl font-semibold group-data-[collapsible=icon]:hidden">Fenra</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} isOnFreePlan={isOnFreePlan} />
        {organizationItems.length > 0 && <NavOrganization items={organizationItems} />}
        <NavSecondary items={data.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter className="pt-0">
        <NavUser user={userData} />
      </SidebarFooter>
    </Sidebar>
  )
}
